import React, { useState, useEffect } from 'react';

import { Images } from 'assets/images';
import { contestLanguages } from 'constants/languages';
import { distributeTrophies } from 'functions/distributeTrophies';
import RoundedSearchBar from 'components/RoundedSearchBar/RoundedSearchBar';
import { Spinner } from 'components/Spinner/Spinner';
import Dropdown from 'components/Dropdown/Dropdown';

import RankListPlayBack from './RankListPlayBack';
import RankListPlayAgainst from './RankListPlayAgainst';
import {
  SortArrows,
  TableContainer,
  TableRow,
  TableCell,
  TableActions,
  ChallengeTableHeader,
  NameLink,
  ViewGameContainer,
  TrendIcon,
  LanguageIcon,
  SearchBarContainer,
  AvatarImage,
  ActionButton,
  LanguageSelectContainer,
} from './styles';

export function RankList({
  contestId,
  isTournament,
  rankList,
  gamePlay,
  tournamentDetails,
  pixiLoadingStatus,
  isOfficialChallenge,
  isHackathon,
  onSelectedRow,
  isDarkMode,
}) {
  const languages = contestLanguages;

  const [filteredRankList, setFilteredRankList] = useState(rankList);
  const [isAscending, setIsAscending] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(contestLanguages[0]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [nameQuery, setNameQuery] = useState('');
  const [humanInput, setHumanInput] = useState(false);

  useEffect(() => {
    setFilteredRankList(isOfficialChallenge ? distributeTrophies(rankList) : rankList);
  }, [rankList, isOfficialChallenge]);

  const sortRankList = () => {
    let sortedData = filteredRankList.slice().sort((a, b) => {
      return isAscending ? b.maxScore - a.maxScore : a.maxScore - b.maxScore;
    });
    setFilteredRankList(sortedData);
    setIsAscending(!isAscending);
    setSelectedRowIndex(null);
  };

  const sortRatings = () => {
    let sortedData = filteredRankList.slice().sort((a, b) => {
      if (a?.eligibleStatus?.eligible === false) return 1;
      if (b?.eligibleStatus?.eligible === false) return -1;
      if (a.rating === null) return isAscending ? 1 : -1;
      if (b.rating === null) return isAscending ? -1 : 1;
      return isAscending ? b.rating - a.rating : a.rating - b.rating;
    });
    setFilteredRankList(sortedData);
    setIsAscending(!isAscending);
    setSelectedRowIndex(null);
  };

  const searchByLanguage = (language) => {
    if (language) {
      if (!language.value) {
        setSelectedLanguage(contestLanguages[0]);
        setFilteredRankList(rankList);
      } else {
        let updatedList = rankList.filter((participants) => {
          if (language.value.toLowerCase() !== contestLanguages[1].value.toLowerCase()) {
            if (participants.language.toLowerCase() === language.value.toLowerCase()) {
              return participants;
            } else return false;
          } else if (participants.language.toLowerCase() === language.value.toLowerCase()) {
            return participants;
          } else return false;
        });
        setSelectedLanguage(language);
        setFilteredRankList(updatedList);
      }
      setSelectedRowIndex(null);
    }
  };

  const getDeviconIconClassName = (language) => {
    let iconLanguage;
    switch (language) {
      case 'go':
        return `devicon-go-original-wordmark colored`;
      case 'c_sharp':
        iconLanguage = 'csharp';
        break;
      case 'cpp':
        iconLanguage = 'cplusplus';
        break;
      default:
        iconLanguage = language;
    }
    return `devicon-${iconLanguage}-plain colored`;
  };

  const selectedRow = (index, item, humanInput = false) => {
    if (selectedRowIndex === index) {
      setSelectedRowIndex(null);
      setHumanInput(false);
    } else {
      setSelectedRowIndex(index);
      setHumanInput(humanInput);
      if (!humanInput) onSelectedRow(item);
    }
  };

  const renderGame = () => {
    if (humanInput) {
      return (
        <RankListPlayAgainst
          testImplementation={tournamentDetails}
          contestId={contestId}
          opponentId={filteredRankList[selectedRowIndex].accountId}
        />
      );
    } else if (!pixiLoadingStatus) {
      return <RankListPlayBack matchData={gamePlay} testImplementation={tournamentDetails} />;
    } else {
      return (
        <div style={{ padding: 10 }}>
          <Spinner />
        </div>
      );
    }
  };

  const renderLanguageSelect = () => (
    <LanguageSelectContainer>
      <Dropdown
        options={languages}
        icon={selectedLanguage.icon}
        value={selectedLanguage.label}
        onChange={searchByLanguage}
      />
    </LanguageSelectContainer>
  );

  const renderTableBody = () => {
    let count = 0;
    return filteredRankList.map((item) => {
      if (item.name.toLowerCase().search(nameQuery) === -1) return false;
      count++;
      return (
        <div key={count}>
          <ChallengeTableHeader isGrey={count % 2 === 1} className="row py-3">
            <TableCell>{count}</TableCell>
            <TableCell alignLeft flexWidth="45">
              <AvatarImage
                src={item.avatar?.search('/images') !== 0 ? item.avatar : Images.defaultAvatar}
                loading="lazy"
                alt={item.name}
              ></AvatarImage>
              {item.accountId ? <NameLink href={`/profile?id=${item.accountId}`}>{item.name}</NameLink> : item.name}
            </TableCell>
            <TableCell alignLeft flexWidth="25" mobileFlexWidth="32">
              <LanguageIcon className={getDeviconIconClassName(item.language)} />
              {item.languageVersion}
            </TableCell>
            <TableCell flexWidth="16">{item.maxScore}</TableCell>
            <TableCell mobileHide>{item.numSubmits}</TableCell>
          </ChallengeTableHeader>
        </div>
      );
    });
  };

  const renderChallengeTable = () => {
    return (
      <TableContainer>
        <TableRow>
          <TableCell>#</TableCell>
          <TableCell flexWidth="45">Name</TableCell>
          <TableCell flexWidth="25" mobileFlexWidth="32">
            {renderLanguageSelect()}
          </TableCell>
          <TableCell flexWidth="16" isScore onClick={() => sortRankList()}>
            {isTournament ? 'Rating' : 'Score'}{' '}
            <SortArrows alt="arrow" src={isAscending ? Images.downArrow : Images.upArrow} />
          </TableCell>
          <TableCell mobileHide>Submits</TableCell>
        </TableRow>
        {renderTableBody()}
      </TableContainer>
    );
  };

  const renderTournamentTopList = () => {
    let count = 0;
    return filteredRankList.map((item, index) => {
      if (item.name.toLowerCase().search(nameQuery) === -1) return false;
      count++;
      return (
        <div className={isHackathon ? 'row' : ''} key={count}>
          <ChallengeTableHeader
            isGrey={count % 2 === 1}
            ineligible={item?.eligibleStatus?.eligible === false}
            className="row"
          >
            <TableCell>{count}</TableCell>
            <TableCell flexWidth={isHackathon ? '37' : '33'} mobileFlexWidth={isHackathon ? '33' : '41'} alignLeft>
              <AvatarImage
                src={item.avatar?.search('/images') !== 0 ? item.avatar : Images.defaultAvatar}
                loading="lazy"
                alt={item.name}
              />
              {'trend' in item && (
                <TrendIcon
                  isHackathon={isHackathon}
                  isUp={item.trend === 1}
                  isDown={item.trend === -1}
                  data-toggle="tooltip"
                  title="Position as compare to last round"
                />
              )}

              {item.accountId ? <NameLink href={`/profile?id=${item.accountId}`}>{item.name}</NameLink> : item.name}
            </TableCell>
            <TableCell flexWidth={isHackathon ? '24' : '20'} mobileFlexWidth="36" alignLeft>
              <LanguageIcon className={getDeviconIconClassName(item.language)} isHackathon={isHackathon} />
              {item.languageVersion}
            </TableCell>
            <TableCell flexWidth={isHackathon ? '24' : '16'}>
              {item?.eligibleStatus?.eligible === true
                ? item.rating === null
                  ? 'Not ranked'
                  : Math.round(item.rating)
                : item?.eligibleStatus?.reason}
            </TableCell>
            <TableCell flexWidth="8" mobileHide>
              {item.numSubmits}
            </TableCell>
            {!isHackathon && (
              <TableCell flexWidth="16" mobileHide>
                <TableActions>
                  <ActionButton isDarkMode={isDarkMode} onClick={() => selectedRow(index, item)}>
                    {selectedRowIndex === index ? 'Hide Match' : 'View Match'}
                  </ActionButton>
                  {selectedRowIndex !== index &&
                    tournamentDetails &&
                    tournamentDetails?.configCanvas?.canvasHumanAdapter && (
                      <ActionButton isDarkMode={isDarkMode} onClick={() => selectedRow(index, item, true)}>
                        Play Against
                      </ActionButton>
                    )}
                </TableActions>
              </TableCell>
            )}
          </ChallengeTableHeader>
          <ViewGameContainer id="viewGame">{selectedRowIndex === index ? renderGame() : null}</ViewGameContainer>
        </div>
      );
    });
  };

  const renderTournamentTable = () => {
    return (
      <TableContainer>
        <TableRow>
          <TableCell>#</TableCell>
          <TableCell flexWidth={isHackathon ? '37' : '33'} mobileFlexWidth={isHackathon ? '33' : '41'}>
            Name
          </TableCell>
          <TableCell flexWidth={isHackathon ? '24' : '20'} mobileFlexWidth="36">
            {renderLanguageSelect()}
          </TableCell>
          <TableCell flexWidth={isHackathon ? '24' : '16'} isScore="true" onClick={() => sortRatings()}>
            Rating <SortArrows alt="arrow" src={isAscending ? Images.downArrow : Images.upArrow} />
          </TableCell>
          <TableCell flexWidth="8" mobileHide>
            Submits
          </TableCell>
          {!isHackathon && <TableCell flexWidth="16" mobileHide />}
        </TableRow>
        {renderTournamentTopList()}
      </TableContainer>
    );
  };

  return (
    <div>
      {!isHackathon && (
        <SearchBarContainer>
          <RoundedSearchBar
            value={nameQuery}
            onChange={(e) => setNameQuery(e.toLowerCase())}
            placeholder="Search by name..."
          />
        </SearchBarContainer>
      )}
      {isTournament ? renderTournamentTable() : renderChallengeTable()}
    </div>
  );
}
